import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Paper,
  Button,
  Stack,
  Alert,
} from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

// Import all our mock components
import ResearchUploadMock from './ResearchUploadMock';
import ResearchCriteriaMock from './ResearchCriteriaMock';
import ResearchReviewMock from './ResearchReviewMock';
import ColumnBuilderMock from './ColumnBuilderMock';
import ConversationalHelperMock from './ConversationalHelperMock';
import TemplateSelectorMock from './TemplateSelectorMock';
import CreditEstimatorMock from './CreditEstimatorMock';
import CardBasedResearchCriteria from './CardBasedResearchCriteria';
import { mockUploadedFile, mockTemplates } from './mockData';

const theme = createTheme({
  palette: {
    mode: 'dark',
    // Wyspur Brand Colors (matching App.js)
    primary: {
      main: '#0095FF', // --wyspur-blue (kept/positive)
    },
    secondary: {
      main: '#E83E8C', // --wyspur-pink (excluded/negative)
    },
    success: {
      main: '#0095FF', // Changed from green to Wyspur blue
    },
    error: {
      main: '#E83E8C', // Changed from red to Wyspur pink
    },
    warning: {
      main: '#FFD700', // --wyspur-yellow
    },
    info: {
      main: '#AB47BC', // --wyspur-purple
    },
    background: {
      default: '#0D0D21', // --wyspur-navy
      paper: '#161c3e',   // --wyspur-surface
    },
    text: {
      primary: '#FFFFFF', // --wyspur-text
      secondary: 'rgba(255, 255, 255, 0.7)',
    },
    divider: 'rgba(0,0,0,0)', // Removed horizontal divider lines
    
    // Custom Wyspur palette
    wyspur: {
      navy: '#0D0D21',    // Main page background
      surface: '#161c3e', // Cards / tables 
      text: '#FFFFFF',    // Primary text
      blue: '#0095FF',    // Positive / "Kept"
      pink: '#E83E8C',    // Negative / "Excluded"
      yellow: '#faa32c',  // Icons / progress dots (NEW YELLOW)
      purple: '#de4097',  // Secondary accent (NEW PURPLE)
      border: '#3C3C5A',  // Table grid / outlines
    },
  },
});

const ResearchMockDemo = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [currentStep, setCurrentStep] = useState(1);
  const [uploadedFile, setUploadedFile] = useState(mockUploadedFile);
  const [criteria, setCriteria] = useState({
    mustHaves: '',
    niceToHaves: '',
    disqualifications: '',
  });
  const [enrichmentColumns, setEnrichmentColumns] = useState([]);
  const [showColumnBuilder, setShowColumnBuilder] = useState(false);
  const [showConversationalHelper, setShowConversationalHelper] = useState(false);

  const tabs = [
    { label: '3-Step Flow', component: 'flow' },
    { label: 'Upload Step', component: ResearchUploadMock },
    { label: 'Criteria Step', component: ResearchCriteriaMock },
    { label: 'Card-Based Criteria', component: CardBasedResearchCriteria },
    { label: 'Review Step', component: ResearchReviewMock },
    { label: 'Column Builder', component: ColumnBuilderMock },
    { label: 'Conversational Helper', component: ConversationalHelperMock },
    { label: 'Template Selector', component: TemplateSelectorMock },
    { label: 'Credit Estimator', component: CreditEstimatorMock },
  ];

  const handleAddEnrichmentColumn = (column) => {
    setEnrichmentColumns([...enrichmentColumns, column]);
  };

  const handleApplyCriteria = (newCriteria) => {
    setCriteria(newCriteria);
  };

  const renderFlowDemo = () => (
    <Box>
      <Alert severity="info" sx={{ mb: 3 }}>
        This demonstrates the complete 3-step flow. Use the navigation buttons to move between steps.
      </Alert>
      
      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <Button
          variant={currentStep === 1 ? 'contained' : 'outlined'}
          onClick={() => setCurrentStep(1)}
        >
          Step 1: Upload
        </Button>
        <Button
          variant={currentStep === 2 ? 'contained' : 'outlined'}
          onClick={() => setCurrentStep(2)}
        >
          Step 2: Criteria
        </Button>
        <Button
          variant={currentStep === 3 ? 'contained' : 'outlined'}
          onClick={() => setCurrentStep(3)}
        >
          Step 3: Review
        </Button>
      </Stack>

      {/* Wrap in Paper to match List Cleaner structure */}
      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        {currentStep === 1 && (
          <ResearchUploadMock
            onNext={() => setCurrentStep(2)}
            onFileUpload={setUploadedFile}
            uploadedFile={uploadedFile}
          />
        )}
        
        {currentStep === 2 && (
          <ResearchCriteriaMock
            onNext={() => setCurrentStep(3)}
            onBack={() => setCurrentStep(1)}
            uploadedFile={uploadedFile}
            criteria={criteria}
            enrichmentColumns={enrichmentColumns}
          />
        )}
        
        {currentStep === 3 && (
          <ResearchReviewMock
            onNext={() => alert('Would redirect to Activity History')}
            onBack={() => setCurrentStep(2)}
            criteria={criteria}
            enrichmentColumns={enrichmentColumns}
            uploadedFile={uploadedFile}
          />
        )}
      </Paper>
    </Box>
  );

  const renderComponent = () => {
    const currentTab = tabs[activeTab];
    
    if (currentTab.component === 'flow') {
      return renderFlowDemo();
    }

    const Component = currentTab.component;
    
    switch (currentTab.label) {
      case 'Upload Step':
        return (
          <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
            <Component
              onNext={() => alert('Next step')}
              onFileUpload={setUploadedFile}
              uploadedFile={uploadedFile || mockUploadedFile}
            />
          </Paper>
        );
        
      case 'Criteria Step':
        return (
          <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
            <Component
              onNext={() => alert('Next step')}
              onBack={() => alert('Previous step')}
              uploadedFile={uploadedFile}
              criteria={criteria}
              enrichmentColumns={enrichmentColumns}
            />
          </Paper>
        );
        
      case 'Card-Based Criteria':
        return (
          <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
            <Component />
          </Paper>
        );
        
      case 'Review Step':
        return (
          <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
            <Component
              onNext={() => alert('Start research')}
              onBack={() => alert('Previous step')}
              criteria={criteria}
              enrichmentColumns={enrichmentColumns}
              uploadedFile={uploadedFile}
            />
          </Paper>
        );
        
      case 'Column Builder':
        return (
          <Component
            open={true}
            onClose={() => alert('Close dialog')}
            onAddColumn={handleAddEnrichmentColumn}
          />
        );
        
      case 'Conversational Helper':
        return (
          <Component
            open={true}
            onClose={() => alert('Close dialog')}
            onApplyCriteria={handleApplyCriteria}
          />
        );
        
      case 'Template Selector':
        return (
          <Component
            onSelectTemplate={(template) => alert(`Selected: ${template.name}`)}
            currentCriteria={criteria}
            onSaveAsTemplate={(template) => alert(`Saved: ${template.name}`)}
          />
        );
        
      case 'Credit Estimator':
        return (
          <Component
            websiteCount={uploadedFile.rowCount}
            enrichmentColumns={enrichmentColumns}
            userCredits={10000}
            showDetailedBreakdown={true}
          />
        );
        
      default:
        return <Component />;
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {/* Match List Cleaner container structure */}
      <Container maxWidth={false} sx={{ mt: 4, mb: 4, ml: 2, mr: 2 }}>
        <Typography variant="h3" gutterBottom align="center">
          Research Agent Mock Components
        </Typography>
        <Typography variant="h6" color="text.secondary" align="center" sx={{ mb: 4 }}>
          Interactive demo of the new 3-step Research Agent interface
        </Typography>

        <Paper 
          variant="outlined" 
          sx={{ 
            p: 2, 
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            bgcolor: 'background.paper' // Ensure Paper uses the correct background
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
          >
            {tabs.map((tab, index) => (
              <Tab key={index} label={tab.label} />
            ))}
          </Tabs>

          <Box sx={{ 
            overflow: 'auto',
            flex: 1,
            minHeight: 600
          }}>
            {renderComponent()}
          </Box>
        </Paper>

        {/* Debug Info */}
        <Paper sx={{ mt: 3, p: 2, bgcolor: 'grey.50' }}>
          <Typography variant="h6" gutterBottom>
            Debug Info
          </Typography>
          <Typography variant="body2">
            Uploaded File: {uploadedFile?.name} ({uploadedFile?.rowCount} rows)
            <br />
            Enrichment Columns: {enrichmentColumns.length}
            <br />
            Current Criteria: {criteria.mustHaves ? 'Set' : 'Not set'}
          </Typography>
        </Paper>
      </Container>
    </ThemeProvider>
  );
};

export default ResearchMockDemo;